import type { SeacloudConfig } from './types.js';
import { SeacloudError } from './types.js';

/**
 * 检测当前是否在 iframe 中
 */
function isInIframe(): boolean {
  try {
    return typeof (globalThis as any).window !== 'undefined' &&
      (globalThis as any).window.self !== (globalThis as any).window.top;
  } catch (e) {
    // 如果访问 window.top 抛出异常，说明在跨域 iframe 中
    return true;
  }
}

/**
 * 通过 PostMessage 从父页面获取 token
 *
 * 此函数允许接收来自任何源的 PostMessage 消息，便于跨域 iframe 集成。
 * 使用 seaverse 协议与父页面通信：
 * - 发送: { type: 'seaverse:get_token' }
 * - 接收: { type: 'seaverse:token', payload: { accessToken: string, expiresIn: number } }
 * - 错误: { type: 'seaverse:error', error: string }
 *
 * @param timeout 超时时间（毫秒），默认 5000ms
 * @returns token 或 null
 */
async function getTokenFromParent(timeout: number = 5000): Promise<string | null> {
  if (!isInIframe()) {
    return null;
  }

  return new Promise((resolve) => {
    const messageHandler = (event: MessageEvent) => {
      // 验证消息格式
      if (event.data && event.data.type === 'seaverse:token') {
        cleanup();
        // 从 payload.accessToken 中获取 token
        const token = event.data.payload?.accessToken;
        console.log('[SeaCloud SDK] Successfully received token from parent page');
        resolve(token || null);
      } else if (event.data && event.data.type === 'seaverse:error') {
        // 处理错误响应
        cleanup();
        console.warn('[SeaCloud SDK] Error getting token from parent:', event.data.error);
        resolve(null);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      console.warn('[SeaCloud SDK] Timeout waiting for token from parent page');
      resolve(null);
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
      (globalThis as any).window.removeEventListener('message', messageHandler);
    };

    // 监听父页面的响应（不限制来源）
    (globalThis as any).window.addEventListener('message', messageHandler);

    // 向父页面发送请求（允许任何源接收）
    try {
      console.log('[SeaCloud SDK] Requesting token from parent page...');
      (globalThis as any).window.parent.postMessage(
        { type: 'seaverse:get_token' },
        '*'  // 允许任何源，支持跨域场景
      );
    } catch (e) {
      cleanup();
      console.warn('[SeaCloud SDK] Failed to send token request to parent:', e);
      resolve(null);
    }
  });
}

/**
 * 通过 PostMessage 从父页面获取 Host
 *
 * 此函数允许接收来自任何源的 PostMessage 消息，便于跨域 iframe 集成。
 * 使用 seaverse 协议与父页面通信：
 * - 发送: { type: 'seaverse:get_host' }
 * - 接收: { type: 'seaverse:host', payload: { host: string } }
 * - 错误: { type: 'seaverse:error', error: string }
 *
 * @param timeout 超时时间（毫秒），默认 5000ms
 * @returns host (如 window.location.origin) 或 null
 */
export async function getHostFromParent(timeout: number = 5000): Promise<string | null> {
  if (!isInIframe()) {
    return null;
  }

  return new Promise((resolve) => {
    const messageHandler = (event: MessageEvent) => {
      // 验证消息格式
      if (event.data && event.data.type === 'seaverse:host') {
        cleanup();
        // 从 payload.host 中获取 host
        const host = event.data.payload?.host;
        resolve(host || null);
      } else if (event.data && event.data.type === 'seaverse:error') {
        // 处理错误响应
        cleanup();
        console.warn('[SeaCloud SDK] Error getting host from parent:', event.data.error);
        resolve(null);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
      (globalThis as any).window.removeEventListener('message', messageHandler);
    };

    // 监听父页面的响应（不限制来源）
    (globalThis as any).window.addEventListener('message', messageHandler);

    // 向父页面发送请求（允许任何源接收）
    try {
      (globalThis as any).window.parent.postMessage(
        { type: 'seaverse:get_host' },
        '*'  // 允许任何源，支持跨域场景
      );
    } catch (e) {
      cleanup();
      resolve(null);
    }
  });
}

/**
 * 通过 PostMessage 从父页面获取环境标识
 *
 * 此函数允许接收来自任何源的 PostMessage 消息，便于跨域 iframe 集成。
 * 使用 seaverse 协议与父页面通信：
 * - 发送: { type: 'seaverse:get_env' }
 * - 接收: { type: 'seaverse:env', payload: { env: string } }
 * - 错误: { type: 'seaverse:error', error: string }
 *
 * @param timeout 超时时间（毫秒），默认 5000ms
 * @returns 环境标识字符串 (如 "develop", "production") 或 null
 */
export async function getEnvFromParent(timeout: number = 5000): Promise<string | null> {
  if (!isInIframe()) {
    return null;
  }

  return new Promise((resolve) => {
    const messageHandler = (event: MessageEvent) => {
      // 验证消息格式
      if (event.data && event.data.type === 'seaverse:env') {
        cleanup();
        // 从 payload.env 中获取环境标识
        const env = event.data.payload?.env;
        resolve(env || null);
      } else if (event.data && event.data.type === 'seaverse:error') {
        // 处理错误响应
        cleanup();
        console.warn('[SeaCloud SDK] Error getting env from parent:', event.data.error);
        resolve(null);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
      (globalThis as any).window.removeEventListener('message', messageHandler);
    };

    // 监听父页面的响应（不限制来源）
    (globalThis as any).window.addEventListener('message', messageHandler);

    // 向父页面发送请求（允许任何源接收）
    try {
      (globalThis as any).window.parent.postMessage(
        { type: 'seaverse:get_env' },
        '*'  // 允许任何源，支持跨域场景
      );
    } catch (e) {
      cleanup();
      resolve(null);
    }
  });
}

/**
 * 动态获取 API Token
 * 优先级：
 * 1. 传入的 apiKey
 * 2. localStorage auth_token (浏览器环境)
 * 3. 环境变量 API_SERVICE_TOKEN (Node.js 环境)
 * 4. 通过 PostMessage 从父页面获取 (iframe 环境)
 * 5. 没有找到 → 抛出错误
 *
 * @param providedApiKey 用户提供的 apiKey（可选）
 * @returns API Token
 */
export async function getApiToken(providedApiKey?: string): Promise<string | undefined> {
  // 1. 如果提供了 apiKey，直接使用
  if (providedApiKey) {
    console.log('[SeaCloud SDK] Using provided apiKey');
    return providedApiKey;
  }

  // 2. 尝试从 localStorage 获取 (浏览器环境)
  if (typeof (globalThis as any).window !== 'undefined' && typeof (globalThis as any).localStorage !== 'undefined') {
    try {
      console.log('[SeaCloud SDK] Trying to get token from localStorage...');
      const localStorageToken = (globalThis as any).localStorage.getItem('auth_token');
      if (localStorageToken) {
        console.log('[SeaCloud SDK] Successfully got token from localStorage');
        return localStorageToken;
      }
    } catch (e) {
      console.warn('[SeaCloud SDK] Error getting token from localStorage:', e);
      // localStorage might be blocked or unavailable
    }
  }

  // 3. 尝试从环境变量获取 (Node.js 环境)
  if (typeof process !== 'undefined' && process.env?.API_SERVICE_TOKEN) {
    console.log('[SeaCloud SDK] Trying to get token from environment variable...');
    return process.env.API_SERVICE_TOKEN;
  }

  // 4. 如果在 iframe 中，尝试通过 PostMessage 从父页面获取
  if (typeof (globalThis as any).window !== 'undefined') {
    console.log('[SeaCloud SDK] Trying to get token from parent page...');
    const parentToken = await getTokenFromParent();
    if (parentToken) {
      return parentToken;
    }
  }

  // 5. 返回''，让调用方处理
  return '';

  // // 5. 没有找到任何 token，抛出错误
  // throw new Error(
  //   'SeaCloud SDK: No API token found. ' +
  //   'Please ensure token is available in localStorage.getItem("auth_token") (browser) ' +
  //   'or process.env.API_SERVICE_TOKEN (Node.js), ' +
  //   'or initialize with initSeacloud({ apiKey: "your-token" }).'
  // );
}

/**
 * 检查 host 是否为开发环境
 */
export function checkIsDevelopmentHost(host: string): boolean {
  const devHostPatterns = ['localhost', '127.0.0.1', ':3000', ':8080', 'seaverse.dev', "seaart.dev", "dev.seaspark"];
  return devHostPatterns.some(pattern => host.includes(pattern));
}

/**
 * 统一获取 baseUrl 的逻辑
 *
 * 判断优先级：
 * 1. 用户显式传入 baseUrl
 * 2. 环境变量 SEACLOUD_BASE_URL
 * 3. 当前 host 判断（优先）- 如果 host 包含开发环境特征，直接返回开发 URL
 * 4. iframe 环境 - 如果在 iframe 中且 host 不是开发环境，从父页面获取环境标识
 * 5. 默认 - 使用正式环境 URL
 *
 * @param userBaseUrl 用户传入的 baseUrl（可选）
 * @returns baseUrl 字符串
 */
export async function getBaseUrl(userBaseUrl?: string): Promise<string> {
  // 1. 用户显式传入 baseUrl，直接使用
  if (userBaseUrl) {
    return userBaseUrl;
  }

  // 2. 使用环境变量
  if (typeof process !== 'undefined' && process.env?.SEACLOUD_BASE_URL) {
    return process.env.SEACLOUD_BASE_URL;
  }

  // 3. 优先检查当前 host（无论是否在 iframe 中）
  const currentHost = typeof globalThis !== 'undefined' &&
    typeof (globalThis as any).window !== 'undefined'
    ? (globalThis as any).window.location.host
    : '';

  // 如果当前 host 明确是开发环境，直接使用（不等待父页面响应）
  if (checkIsDevelopmentHost(currentHost)) {
    console.log('[SeaCloud SDK] 检测到开发环境（host: ' + currentHost + '），使用开发 baseUrl');
    return 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';
  }

  // 4. 如果在 iframe 中且当前 host 不是开发环境，从父页面获取环境标识
  if (isInIframe()) {
    console.log('[SeaCloud SDK] 在 iframe 中但当前 host 不是开发环境，询问父页面...');
    const parentEnv = await getEnvFromParent(3000);

    if (parentEnv === 'develop') {
      console.log('[SeaCloud SDK] 父页面返回开发环境，使用开发 baseUrl');
      return 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';
    } else {
      // 没获取到或获取到的不是 develop，使用线上环境
      console.log('[SeaCloud SDK] 使用线上 baseUrl（父页面未返回 develop）');
      return 'https://seaspark-seacloud-proxy-rs.seaspark.ai';
    }
  }

  // 5. 非 iframe 环境且不是开发 host，使用正式环境
  console.log('[SeaCloud SDK] 使用正式环境 baseUrl');
  return 'https://seaspark-seacloud-proxy-rs.seaspark.ai';
}

/**
 * 创建 SDK 配置
 * @param options 配置选项
 * @returns 完整的配置对象
 */
export async function createConfig(options: SeacloudConfig = {}): Promise<Required<SeacloudConfig>> {
  // apiKey 允许为 undefined，在实际请求时才异步获取
  const apiKey = options.apiKey;

  // 使用统一的 getBaseUrl 函数获取 baseUrl
  const baseUrl = await getBaseUrl(options.baseUrl);

  // 使用全局 fetch 或自定义实现
  // 在浏览器环境中，fetch 需要绑定到 globalThis 以避免 "Illegal invocation" 错误
  const fetchImpl = options.fetch || (globalThis.fetch ? globalThis.fetch.bind(globalThis) : undefined);

  if (!fetchImpl) {
    throw new Error('fetch is not available. Please provide a fetch implementation in config or upgrade to Node.js 18+');
  }

  return {
    apiKey: apiKey || '', // 提供默认空字符串，实际请求时会动态获取
    baseUrl,
    fetch: fetchImpl,
    timeout: options.timeout || 30000,
    xProject: options.xProject || 'SeaVerse',
  };
}

/**
 * 验证配置
 * @param config 配置对象
 * @throws 如果配置无效
 */
export function validateConfig(config: Required<SeacloudConfig>): void {
  // 移除 API key 验证，允许空 key 传递给后端处理

  if (!config.baseUrl) {
    throw new Error('baseUrl is required');
  }

  if (typeof config.fetch !== 'function') {
    throw new Error('fetch must be a function');
  }
}

/**
 * 向父页面发送 seaverse:need_recharge 消息
 *
 * 使用场景：SDK 打包的单页应用以 iframe 形式嵌入第三方页面，
 * 当检测到余额不足时通过 postMessage 通知父页面处理（如打开充值弹窗）。
 *
 * 消息格式: { type: 'seaverse:need_recharge', payload: { reason, code } }
 *
 * @param reason 触发原因
 * @param code 错误码（如 402）
 */
export function notifyParentNeedRecharge(reason: string, code?: number): void {
  const win = typeof (globalThis as any).window !== 'undefined'
    ? (globalThis as any).window
    : undefined;
  if (!win) return;

  const payload = { reason, code };

  // 在当前 window 派发 CustomEvent，方便页面内部也能监听
  try {
    win.dispatchEvent(
      new CustomEvent('seaverse:need_recharge', { detail: payload }),
    );
  } catch (_) { /* CustomEvent 不可用时静默忽略 */ }

  // 向父页面发送 postMessage
  if (!isInIframe()) {
    console.warn('[SeaCloud SDK] notifyParentNeedRecharge: 当前不在 iframe 中，postMessage 未发送');
    return;
  }

  try {
    win.parent.postMessage(
      { type: 'seaverse:need_recharge', payload },
      '*',
    );
    console.log('[SeaCloud SDK] 已向父页面发送 seaverse:need_recharge', payload);
  } catch (e) {
    console.warn('[SeaCloud SDK] postMessage 发送失败:', e);
  }
}

const INSUFFICIENT_BALANCE_KEYWORDS = [
  'insufficient',
  'balance',
  'credit',
  '余额不足',
  '积分不足',
  '额度不足',
  'quota exceeded',
  'payment required',
];

/**
 * 检测错误是否为余额不足，如果是则通知父页面
 *
 * 检测规则：
 * 1. SeacloudError.statusCode === 402
 * 2. 错误消息 / 响应体包含余额不足关键词
 */
export function checkAndNotifyInsufficientBalance(error: unknown): void {
  console.log('[SeaCloud SDK] checkAndNotifyInsufficientBalance called', error);
  if (!(error instanceof SeacloudError)) return;

  if (error.statusCode === 402) {
    console.log('[SeaCloud SDK] HTTP 状态码 402，触发充值通知');
    notifyParentNeedRecharge(error.message, 402);
    return;
  }

  const msg = (error.message + ' ' + (error.response || '')).toLowerCase();
  if (INSUFFICIENT_BALANCE_KEYWORDS.some(kw => msg.includes(kw))) {
    console.log('[SeaCloud SDK] 错误消息匹配余额不足关键词，触发充值通知');
    notifyParentNeedRecharge(error.message, error.statusCode);
  }
}

/**
 * 检测 HTTP 200 响应体中的业务级余额不足错误
 *
 * 部分后端在余额不足时仍返回 HTTP 200，但在 JSON body 中用 code 字段标识错误，例如：
 * { "code": 402, "message": "Insufficient credits...", "data": { "type": "credits_error" } }
 *
 * 此函数在 response.json() 后调用，检测 body.code / body.message 是否表示余额不足。
 */
export function checkResponseBodyForInsufficientBalance(body: any): void {
  if (!body || typeof body !== 'object') return;

  console.log('[SeaCloud SDK] checkResponseBodyForInsufficientBalance called, body.code =', body.code, ', body.message =', body.message);

  if (body.code === 402) {
    console.log('[SeaCloud SDK] 响应体 code=402，触发充值通知');
    notifyParentNeedRecharge(body.message || 'Insufficient balance (code 402)', 402);
    return;
  }

  if (body.message && typeof body.message === 'string') {
    const msg = body.message.toLowerCase();
    if (INSUFFICIENT_BALANCE_KEYWORDS.some(kw => msg.includes(kw))) {
      console.log('[SeaCloud SDK] 响应体 message 匹配余额不足关键词，触发充值通知');
      notifyParentNeedRecharge(body.message, body.code);
    }
  }
}

/**
 * 检测 SSE 流式响应 chunk 中的余额不足错误
 *
 * 支持检测：
 * - chunk.code === 402 （顶层业务错误码）
 * - chunk.error (string 或 { message, code })
 * - chunk.message 包含余额不足关键词
 * - chunk.choices[].content_filter_results.error
 *
 * @returns 是否检测到余额不足
 */
export function checkStreamChunkForInsufficientBalance(chunkData: any): boolean {
  if (!chunkData) return false;

  // 优先检测顶层 code === 402（后端统一格式）
  if (chunkData.code === 402) {
    console.log('[SeaCloud SDK] SSE chunk code=402，触发充值通知', chunkData);
    notifyParentNeedRecharge(chunkData.message || 'Insufficient balance detected in stream', 402);
    return true;
  }

  const textsToCheck: string[] = [];

  // 检测顶层 message
  if (chunkData.message && typeof chunkData.message === 'string') {
    textsToCheck.push(chunkData.message);
  }

  if (chunkData.error) {
    const err = chunkData.error;
    if (typeof err === 'string') {
      textsToCheck.push(err);
    } else if (typeof err === 'object') {
      if (err.message) textsToCheck.push(err.message);
      if (err.code) textsToCheck.push(String(err.code));
    }
  }

  if (Array.isArray(chunkData.choices)) {
    for (const choice of chunkData.choices) {
      const filterResult = choice.content_filter_results || choice.content_filter_result;
      if (filterResult?.error) {
        if (filterResult.error.message) textsToCheck.push(filterResult.error.message);
        if (filterResult.error.code) textsToCheck.push(String(filterResult.error.code));
      }
    }
  }

  const combined = textsToCheck.join(' ').toLowerCase();
  if (combined.includes('402') || INSUFFICIENT_BALANCE_KEYWORDS.some(kw => combined.includes(kw))) {
    console.log('[SeaCloud SDK] SSE chunk 匹配余额不足关键词，触发充值通知', chunkData);
    notifyParentNeedRecharge(textsToCheck[0] || 'Insufficient balance detected in stream');
    return true;
  }

  return false;
}
